import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Subscription } from 'rxjs';
import { Vendor } from 'common';

@Injectable({
    providedIn: 'root'
})
export class AnalyticsService implements OnDestroy {

    /**
     * Prevent installing multiple times.
     */
    private hasInstalled = false;

    /**
     * Prevent multiple subscriptions to router.events and cleanup.
     */
    private routerEventsSub: Subscription | undefined;

    constructor(
        private router: Router,
        @Inject(DOCUMENT) private document: Document,
        @Inject(PLATFORM_ID) private platformId: object,
    ) { }

    public initialize() {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        if (this.hasInstalled) {
            console.warn('Google Analytics already installed');
            return;
        }

        // Dynamically add analytics scripts to document head.
        try {
            const url = 'https://www.googletagmanager.com/gtag/js?id=';
            const gTagScript = this.document.createElement('script');
            gTagScript.async = true;
            gTagScript.src = `${url}${MEASUREMENT_ID}`;
            this.document.head.appendChild(gTagScript);

            const dataLayerScript = this.document.createElement('script');
            dataLayerScript.innerHTML = `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${MEASUREMENT_ID}', {
                    send_page_view: false,
                    cookie_flags: 'SameSite=None;Secure',
                    cookie_domain: window.location.hostname // Ensure the cookie domain matches the current domain
                });
            `;
            this.document.head.appendChild(dataLayerScript);

            this.hasInstalled = true;
            setTimeout(() => {
                this.onRouteChange();
            });
        } catch (e) {
            console.error('Error adding Google Analytics', e);
        }
    }

    /**
     * Track route changes.
     */
    private onRouteChange() {
        // Prevent multiple subscriptions.
        if (!this.routerEventsSub) {
            this.routerEventsSub = this.router.events
                .subscribe(event => {
                    if (event instanceof NavigationEnd) {
                        this.gtag('config', MEASUREMENT_ID, {
                            page_path: event.urlAfterRedirects,
                        });
                    }
                });
        }
    }

    /**
     * Track events.
     *
     * @param {string} action The event name - example: 'click'.
     * @param {string} [eventCategory] The category/event Type - example: 'SCROLL_TO_TOP_CLICKED'.
     * @param {string} [eventLabel] The label that will show up in the dashboard as the events name.
     * @param {string} [value] A short description of what happened.
     */
    public event(action: string, eventCategory?: string, eventLabel?: string, value?: string) {
        this.gtag('event', action, {
            ...(eventCategory && { event_category: eventCategory }),
            ...(eventLabel && { event_label: eventLabel }),
            ...(value && { value: value }),
        });
    }

    /**
     * Sets GA4 user properties for vendor segmentation.
     * Call once after vendor data is loaded — the properties persist across
     * all subsequent events in the session without needing to be repeated.
     */
    public setVendorContext(vendor: Vendor): void {
        this.gtag('set', 'user_properties', {
            vendor_id:   vendor.id,
            vendor_name: vendor.tradingName || vendor.name,
        });
    }

    /**
     * Clears vendor user properties on logout.
     */
    public clearVendorContext(): void {
        this.gtag('set', 'user_properties', {
            vendor_id:   null,
            vendor_name: null,
        });
    }

    /**
     * SSR-safe accessor for the browser `gtag` global.
     */
    // eslint-disable-next-line @typescript-eslint/ban-types
    private gtag(...args: unknown[]): void {
        if (isPlatformBrowser(this.platformId) && typeof (window as any)['gtag'] === 'function') {
            (window as any)['gtag'](...args);
        }
    }

    public ngOnDestroy(): void {
        this.routerEventsSub?.unsubscribe();
    }

}